import { useState, useEffect } from 'react';
import { apiService, BanwordDTO, WhitelistEntryDTO, WhitelistProxyNoticeDTO } from '@/services/api.service';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useAuthStore } from '@/stores/auth.store';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { formatPlayerName } from '@/utils/playerName';
import styles from './SettingsPage.module.scss';

type SettingsTab = 'banwords' | 'whitelist';

interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: string;
}

const tabs: TabConfig[] = [
  { id: 'banwords', label: 'Mots interdits', icon: '🚫' },
  { id: 'whitelist', label: 'Whitelist', icon: '✅' },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('banwords');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Configuration</h1>
          <p className={styles.subtitle}>Gérez les paramètres globaux du serveur</p>
        </div>
      </header>

      <div className={styles.content}>
        {/* Tabs Navigation */}
        <nav className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'banwords' && <BanwordsTab />}
          {activeTab === 'whitelist' && <WhitelistTab />}
        </div>
      </div>
    </div>
  );
}

// ==================== BANWORDS TAB ====================

export function BanwordsTab() {
  const [banwords, setBanwords] = useState<BanwordDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [newWordIsRegex, setNewWordIsRegex] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Delete confirmation state
  const [deletingWord, setDeletingWord] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Search/filter
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadBanwords();
  }, []);

  const loadBanwords = async () => {
    setLoading(true);
    setError(null);

    const result = await apiService.getBanwords();

    if (result.error) {
      setError(result.error);
      setBanwords([]);
    } else if (result.data) {
      // Map the response - l'API retourne un tableau d'objets
      const words = result.data.banwords.map((item) => ({
        word: item.word || '',
        regex: Boolean(item.regex),
      }));
      setBanwords(words);
    }

    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newWord.trim()) return;

    setIsAdding(true);
    setAddError(null);

    const result = await apiService.createBanword(newWord.trim(), newWordIsRegex);

    if (result.error) {
      setAddError(result.error);
      setIsAdding(false);
    } else {
      setShowAddModal(false);
      setNewWord('');
      setNewWordIsRegex(false);
      setIsAdding(false);
      loadBanwords();
    }
  };

  const handleDelete = async () => {
    if (!deletingWord) return;

    setIsDeleting(true);

    const result = await apiService.deleteBanword(deletingWord);

    setIsDeleting(false);
    setDeletingWord(null);

    if (!result.error) {
      loadBanwords();
    }
  };

  const filteredBanwords = banwords.filter((bw) =>
    bw.word.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const regexCount = banwords.filter((bw) => bw.regex).length;
  const normalCount = banwords.length - regexCount;

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Chargement des mots interdits...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <span className={styles.errorIcon}>❌</span>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button className={styles.retryButton} onClick={loadBanwords}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className={styles.banwordsTab}>
      {/* Header with stats and actions */}
      <div className={styles.tabHeader}>
        <div className={styles.tabStats}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{banwords.length}</span>
            <span className={styles.statLabel}>Total</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{normalCount}</span>
            <span className={styles.statLabel}>Mots</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{regexCount}</span>
            <span className={styles.statLabel}>Regex</span>
          </div>
        </div>

        <div className={styles.tabActions}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className={styles.addButton}
            onClick={() => setShowAddModal(true)}
          >
Ajouter
          </button>
        </div>
      </div>

      {/* Banwords List */}
      {filteredBanwords.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>🚫</span>
          <p>{searchQuery ? 'Aucun résultat' : 'Aucun mot interdit configuré'}</p>
          {!searchQuery && (
            <span className={styles.emptyHint}>
              Ajoutez des mots ou expressions qui seront automatiquement filtrés dans le chat.
            </span>
          )}
        </div>
      ) : (
        <div className={styles.banwordsList}>
          {filteredBanwords.map((bw, index) => (
            <div key={index} className={styles.banwordItem}>
              <div className={styles.banwordContent}>
                <span className={styles.banwordWord}>{bw.word}</span>
                {bw.regex && (
                  <span className={styles.regexBadge}>Regex</span>
                )}
              </div>
              <button
                className={styles.deleteButton}
                onClick={() => setDeletingWord(bw.word)}
                title="Supprimer"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Ajouter un mot interdit</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.modalDescription}>
              Ce mot ou expression sera automatiquement filtré dans le chat du serveur.
            </p>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Mot ou expression</label>
              <input
                type="text"
                className={styles.modalInput}
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder="Entrez le mot à interdire..."
                autoFocus
              />
            </div>

            <div className={styles.modalField}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={newWordIsRegex}
                  onChange={(e) => setNewWordIsRegex(e.target.checked)}
                />
                Expression régulière (Regex)
              </label>
              {newWordIsRegex && (
                <span className={styles.fieldHint}>
                  Le mot sera traité comme une expression régulière pour des filtres avancés.
                </span>
              )}
            </div>

            {addError && (
              <div className={styles.modalError}>{addError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowAddModal(false)}
                disabled={isAdding}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButton}
                onClick={handleAdd}
                disabled={isAdding || !newWord.trim()}
              >
                {isAdding ? 'Ajout...' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingWord && (
        <div className={styles.modalOverlay} onClick={() => setDeletingWord(null)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.deleteModalIcon}>🗑️</div>
            <h3 className={styles.deleteModalTitle}>Supprimer ce mot ?</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.deleteModalDescription}>
              Le mot <strong>"{deletingWord}"</strong> ne sera plus filtré dans le chat.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setDeletingWord(null)}
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButtonDanger}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== WHITELIST TAB ====================

/** Entrée de la ModerationAPI qui veut dire « liste allumée » : jamais un joueur (satellite d'avant 1.0.24). */
const STATUS_ENTRY = 'console';

/**
 * Nom changé pendant que la liste est allumée, proxys non prévenus : le joueur ne compte qu'au prochain
 * allumage ou redémarrage du proxy. Absent (liste éteinte, satellite d'avant 1.0.24) : rien à dire.
 */
function warnIfProxyNotNotified(proxy: WhitelistProxyNoticeDTO | null | undefined) {
  if (proxy && !proxy.notified) {
    useNotificationsStore.getState().warning(
      `Enregistré, mais le proxy n'a pas été prévenu : le changement ne comptera qu'au prochain allumage de la liste. ${proxy.message ?? ''}`,
      12000,
    );
  }
}

interface WhitelistTabProps {
  /**
   * Le satellite vide la liste en gardant « Console » (1.0.24 ou plus, état lu par la page). Sinon (satellite plus
   * ancien, état inconnu) le front retire les joueurs un par un : le vidage de l'ancien satellite effaçait aussi
   * « Console », et la liste paraissait éteinte alors que le proxy refusait encore les joueurs.
   */
  serverClearKeepsState?: boolean;
}

export function WhitelistTab({ serverClearKeepsState = false }: WhitelistTabProps = {}) {
  // Droit de lecture seule (<projet>.whitelist:read) : la liste sans ses boutons d'écriture.
  const writable = useAuthStore((s) => s.canWrite)('whitelist');
  const [entries, setEntries] = useState<WhitelistEntryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Delete confirmation state
  const [deletingEntry, setDeletingEntry] = useState<WhitelistEntryDTO | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear all confirmation
  const [showClearModal, setShowClearModal] = useState(false);
  // Groupe de jeu dont la whitelist est vidée (NationsGlory : Nova, Iris) ; null pour Paladium.
  const gameGroup = useActiveGameGroup();
  const [isClearing, setIsClearing] = useState(false);

  // Search/filter
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadWhitelist();
  }, []);

  const loadWhitelist = async () => {
    setLoading(true);
    setError(null);

    const result = await apiService.getWhitelist();

    if (result.error) {
      setError(result.error);
      setEntries([]);
    } else if (result.data) {
      setEntries(result.data.entries.filter((e) => (e.name ?? '').trim().toLowerCase() !== STATUS_ENTRY));
    }

    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;

    setIsAdding(true);
    setAddError(null);

    const result = await apiService.addToWhitelist(newName.trim());

    if (result.error) {
      setAddError(result.error === 'HTTP 409' ? 'Ce joueur est déjà dans la whitelist' : result.error);
      setIsAdding(false);
    } else {
      warnIfProxyNotNotified(result.data?.proxy);
      setShowAddModal(false);
      setNewName('');
      setIsAdding(false);
      loadWhitelist();
    }
  };

  const handleDelete = async () => {
    if (!deletingEntry) return;

    setIsDeleting(true);

    const result = await apiService.removeFromWhitelist(deletingEntry.name);

    setIsDeleting(false);
    setDeletingEntry(null);

    if (!result.error) {
      warnIfProxyNotNotified(result.data?.proxy);
      loadWhitelist();
    }
  };

  const handleClearAll = async () => {
    setIsClearing(true);

    if (serverClearKeepsState) {
      const result = await apiService.clearWhitelist();
      if (!result.error) {
        warnIfProxyNotNotified(result.data?.proxy);
      } else {
        useNotificationsStore.getState().error(result.error, 12000);
      }
    } else {
      const failed: string[] = [];
      for (const entry of entries) {
        const result = await apiService.removeFromWhitelist(entry.name);
        if (result.error) failed.push(formatPlayerName(entry.name, entry.displayName));
      }
      if (failed.length) {
        useNotificationsStore.getState().error(`Retrait impossible pour : ${failed.join(', ')}`, 12000);
      }
    }

    setIsClearing(false);
    setShowClearModal(false);
    loadWhitelist();
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filtre sur le gamertag et le pseudo Recube.
  const filteredEntries = entries.filter((entry) =>
    formatPlayerName(entry.name, entry.displayName).toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Chargement de la whitelist...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <span className={styles.errorIcon}>❌</span>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button className={styles.retryButton} onClick={loadWhitelist}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className={styles.whitelistTab}>
      {/* Header with stats and actions */}
      <div className={styles.tabHeader}>
        <div className={styles.tabStats}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{entries.length}</span>
            <span className={styles.statLabel}>Joueurs</span>
          </div>
        </div>

        <div className={styles.tabActions}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {writable && entries.length > 0 && (
            <button
              className={styles.clearButton}
              onClick={() => setShowClearModal(true)}
            >
              Vider tout
            </button>
          )}
          {writable && (
            <button
              className={styles.addButton}
              onClick={() => setShowAddModal(true)}
            >
              Ajouter
            </button>
          )}
        </div>
      </div>

      {/* Whitelist List */}
      {filteredEntries.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>✅</span>
          <p>{searchQuery ? 'Aucun résultat' : 'Aucun joueur dans la whitelist'}</p>
          {!searchQuery && (
            <span className={styles.emptyHint}>
              Ajoutez des joueurs pour leur permettre de rejoindre le serveur.
            </span>
          )}
        </div>
      ) : (
        <div className={styles.whitelistList}>
          {filteredEntries.map((entry) => (
            <div key={entry.id} className={styles.whitelistItem}>
              <div className={styles.whitelistContent}>
                <span className={styles.whitelistName}>{formatPlayerName(entry.name, entry.displayName)}</span>
                <span className={styles.whitelistDate}>
                  Ajouté le {formatDate(entry.create_at)}
                </span>
              </div>
              {writable && (
                <button
                  className={styles.deleteButton}
                  onClick={() => setDeletingEntry(entry)}
                  title="Retirer de la whitelist"
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Ajouter à la whitelist</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.modalDescription}>
              Ce joueur pourra rejoindre le serveur même si la whitelist est activée.
            </p>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Nom du joueur</label>
              <input
                type="text"
                className={styles.modalInput}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Pseudo Recube ou gamertag du joueur..."
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && !isAdding && newName.trim() && handleAdd()}
              />
            </div>

            {addError && (
              <div className={styles.modalError}>{addError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowAddModal(false)}
                disabled={isAdding}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButton}
                onClick={handleAdd}
                disabled={isAdding || !newName.trim()}
              >
                {isAdding ? 'Ajout...' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingEntry && (
        <div className={styles.modalOverlay} onClick={() => setDeletingEntry(null)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.deleteModalIcon}>🗑️</div>
            <h3 className={styles.deleteModalTitle}>Retirer de la whitelist ?</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.deleteModalDescription}>
              <strong>{formatPlayerName(deletingEntry.name, deletingEntry.displayName)}</strong> ne pourra plus rejoindre le serveur si la whitelist est activée.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setDeletingEntry(null)}
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButtonDanger}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Suppression...' : 'Retirer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Confirmation Modal */}
      {showClearModal && (
        <div className={styles.modalOverlay} onClick={() => setShowClearModal(false)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.deleteModalIcon}>⚠️</div>
            <h3 className={styles.deleteModalTitle}>Vider la whitelist ?</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.deleteModalDescription}>
              Cette action supprimera <strong>{entries.length} joueur{entries.length > 1 ? 's' : ''}</strong> de la whitelist
              {gameGroup ? <> sur <strong>{gameGroup.label}</strong> seulement</> : null}.
              La liste reste activée ou désactivée comme avant. Cette action est irréversible.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowClearModal(false)}
                disabled={isClearing}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButtonDanger}
                onClick={handleClearAll}
                disabled={isClearing}
              >
                {isClearing ? 'Suppression...' : 'Tout supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
